<template>
  <div>
    <b-row>
      <b-col>
        <div class="page-subtitle mt-5 mb-2">Permissions</div>
        <p class="text-muted mb-3">
          The full list of capabilities you can grant through a role. This list
          is managed by Tradie Pass - build a
          <strong>Role</strong> to bundle these together and assign them to your
          team.
        </p>
      </b-col>
    </b-row>

    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="catalogue.length === 0" class="text-muted py-4">
      No permissions available.
    </div>

    <b-card
      v-for="group in catalogue"
      v-else
      :key="group.group"
      class="mb-3"
      no-body
    >
      <b-card-header class="py-2">
        <div class="perm-group__title">{{ groupLabel(group.group) }}</div>
        <small
          v-if="groupHint(group.group)"
          class="perm-group__hint text-muted"
        >
          {{ groupHint(group.group) }}
        </small>
      </b-card-header>
      <b-list-group flush>
        <b-list-group-item
          v-for="perm in group.permissions"
          :key="perm.id"
          class="perm-item d-flex flex-column flex-md-row justify-content-between align-items-start"
        >
          <div class="perm-item__main">
            <div class="perm-item__label">
              {{ permissionLabel(perm) }}
              <b-badge
                v-if="perm.is_project_scoped"
                variant="info"
                class="ml-1"
              >
                Per project
              </b-badge>
            </div>
            <small v-if="perm.description" class="text-muted perm-item__desc">
              {{ perm.description }}
            </small>
          </div>
          <code class="perm-item__key text-muted small">{{ perm.key }}</code>
        </b-list-group-item>
      </b-list-group>
    </b-card>
  </div>
</template>

<script>
import {
  permissionGroupLabel,
  permissionGroupHint,
  permissionItemLabel,
  filterPermissionCatalogue,
  mergeProjectGroups,
} from '@/utils/permissionLabels'

export default {
  name: 'PermissionsCatalogue',

  data() {
    return {
      catalogue: [],
      isLoading: false,
    }
  },

  mounted() {
    this.loadCatalogue()
  },

  methods: {
    async loadCatalogue() {
      this.isLoading = true
      try {
        const { data } = await this.$api.get('company/permissions')
        this.catalogue = mergeProjectGroups(
          filterPermissionCatalogue((data && data.data) || []),
        )
      } catch (e) {
        this.catalogue = []
      } finally {
        this.isLoading = false
      }
    },

    groupLabel(group) {
      return permissionGroupLabel(group)
    },

    groupHint(group) {
      return permissionGroupHint(group)
    },

    permissionLabel(perm) {
      return permissionItemLabel(perm)
    },
  },
}
</script>

<style scoped>
/* ── Type hierarchy ─────────────────────────────────────────────────────
   Group title (heading) → row title (semibold) → definition & key (muted,
   small). Kept consistent across screen sizes; the layout (side-by-side vs
   stacked) changes responsively below. */
.perm-group__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2c3e50;
}
.perm-group__hint {
  display: block;
  margin-top: 0.1rem;
  font-size: 0.75rem;
  line-height: 1.35;
  font-weight: 400;
}
.perm-item__main {
  min-width: 0;
}
.perm-item__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #2c3e50;
}
.perm-item__desc {
  display: block;
  margin-top: 0.1rem;
  font-size: 0.75rem;
  line-height: 1.4;
}
/* The key is a long unbreakable string; let it wrap/break and shrink so it can
   never overflow the row (in either the side-by-side or stacked layout). */
.perm-item__key {
  font-size: 0.7rem;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Phones/tablets: stack the key below the label as a subtle, wrappable chip so
   each row reads cleanly and uses the full width. */
@media (max-width: 767.98px) {
  .perm-item {
    padding: 0.65rem 0.85rem;
  }
  .perm-item__main {
    width: 100%;
  }
  .perm-item__key {
    max-width: 100%;
    margin-top: 0.45rem;
    padding: 2px 8px;
    background: #f4f6f8;
    border-radius: 6px;
    white-space: normal;
    word-break: break-all;
  }
}

/* Section heading spacing scales down on smaller screens. */
@media (max-width: 575.98px) {
  .page-subtitle.mt-5 {
    margin-top: 1.25rem !important;
  }
}
@media (min-width: 576px) and (max-width: 991.98px) {
  .page-subtitle.mt-5 {
    margin-top: 2rem !important;
  }
}
</style>
